// ui/useBotAnnotations.js: bot annotation lifecycle hook (M2.c-9).
//
// Owns the per-seat annotation state. Caller pushes a bot's reasoning
// string via pushAnnotation(seat, text); the hook tracks elapsed time
// via rAF and exposes the render-ready {text, alpha} per seat. When
// every annotation has expired (elapsed >= TOTAL_MS) the rAF loop
// stops itself so we're not burning frames when nothing's on screen.
//
// usePrefersReducedMotion is honored, alpha math from annotation-logic
// returns 1.0 across the hold window without fades.
//
// Public surface:
//   const { annotations, pushAnnotation, clearAll } = useBotAnnotations(reducedMotion);
//
//   annotations: Record<seat, {text, alpha}>  (only currently-visible entries)
//   pushAnnotation(seat, text): void          (no-op if text leaks a card ID)
//   clearAll(): void

import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { computeAlpha, isExpired, TOTAL_MS, containsCardIdLeak } from './annotation-logic.js';

/**
 * @param {boolean} reducedMotion
 * @returns {{
 *   annotations: Record<number, {text:string, alpha:number}>,
 *   pushAnnotation: (seat:number, text:string) => void,
 *   clearAll: () => void,
 * }}
 */
export function useBotAnnotations(reducedMotion) {
  /** @type {[Record<number,{text:string,started_at:number}>, (n:any)=>void]} */
  const [active, setActive] = useState(/** @type {any} */ ({}));
  const [annotations, setAnnotations] = useState(/** @type {Record<number,{text:string,alpha:number}>} */ ({}));
  const rafRef = useRef(/** @type {number|null} */ (null));
  const reducedRef = useRef(reducedMotion);
  reducedRef.current = reducedMotion;

  // Advance the alpha map every frame while anything is active.
  useEffect(() => {
    const seats = Object.keys(active);
    if (seats.length === 0) {
      setAnnotations({});
      return;
    }
    let cancelled = false;
    const tick = (now) => {
      if (cancelled) return;
      /** @type {Record<number,{text:string,alpha:number}>} */
      const next = {};
      /** @type {Record<number,{text:string,started_at:number}>} */
      const stillActive = {};
      let anyAlive = false;
      for (const sid of Object.keys(active)) {
        const seat = Number(sid);
        const entry = active[seat];
        const elapsed = now - entry.started_at;
        if (isExpired(elapsed)) continue;
        const alpha = computeAlpha(elapsed, reducedRef.current);
        next[seat] = { text: entry.text, alpha };
        stillActive[seat] = entry;
        anyAlive = true;
      }
      setAnnotations(next);
      if (anyAlive) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Drop entries from `active` so the effect doesn't re-arm.
        setActive(stillActive);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [active]);

  const pushAnnotation = useCallback((seat, text) => {
    if (!text || containsCardIdLeak(text)) return;  // Pillar 5 leak guard
    setActive((prev) => ({
      ...prev,
      [seat]: { text, started_at: performance.now() },
    }));
  }, []);

  const clearAll = useCallback(() => {
    setActive({});
    setAnnotations({});
  }, []);

  return { annotations, pushAnnotation, clearAll };
}

export const _internalsForTests = { TOTAL_MS };
